/**
 * feat-270 — API client for the curriculum approval gate.
 *
 * AUTHORITY: `decisions/ADR-feat-270-api-contract.md` (Accepted, **r3** at
 * `2be3bd5`). Every shape in this file is transcribed from that document,
 * section by section, and the section is cited on each one. Per ADR-B §6 the
 * frontend slice builds against the ADR and **not** against backend source: if a
 * shape here disagrees with a handler, the ADR is what both of us are wrong
 * about, and it gets fixed there.
 *
 * Backend: `routes/admin_quiz_content.py`, mounted at `prefix="/api"`
 * (`main.py:2426`), so every decorator path below is prefixed `/api`.
 *
 * WHAT THIS FILE DELIBERATELY DOES NOT DO — and it is the load-bearing half.
 * There are now TWO decisions this client is forbidden to make, and r3 added the
 * second by deleting the client-side proxy this file used to carry:
 *
 * 1. It never decides whether something *counts as changed*. ADR-B **§2.1**:
 *    `fields[].changed` is authoritative and must never be re-derived — not with
 *    `!==`, not with `JSON.stringify`, not through `changedSnapshotFields`; the
 *    values come back RAW on both sides, so the manager sees what is actually in
 *    the column; and an item whose every difference normalizes away is not
 *    pending at all, so "a pending lesson with no marked field" is a state that
 *    cannot arrive and must not be coded for. `spec.md` AT-13b, and
 *    `design/NOTES.md` §11c names the failure mode when a client re-derives it.
 *
 * 2. It never decides what **moved**. ADR-B **§3.4.1**: `is_new_since_review` is
 *    server-issued on every item of a 409. The r2 client-side fallback that used
 *    to live at the foot of this file is **deleted, not disabled**, and the
 *    reason is not tidiness — it had a hole, and the hole was the exact
 *    population this feature exists to catch. Pendingness is a function of
 *    *state*, not of audit rows, so an edit that never reaches the write
 *    chokepoint (a raw `UPDATE`, a script, a hotfix, `_restore_criteria`) moves
 *    the fingerprint and writes **no history row at all**: values moved,
 *    `history_id` and `changed_at` unchanged, and the proxy marks nothing. That
 *    is the departing-editor scenario arriving through the one screen built to
 *    catch it, rendering no badge. Second, quieter miss: a client can only judge
 *    the items it RENDERED, and §3.2 truncates at `item_limit` (default 10), so
 *    a 47-item lesson's other 37 would be "not new" by luck rather than by
 *    construction. Spec AT-27b now pins the server field by name.
 *
 * If you find yourself writing a comparison in this file to decide whether
 * something differs or whether it is new, stop — that is the bug.
 */

import { fetchApi, ApiError } from '../utils/api';
import { authHeader } from './authToken';
import { createLimiter } from './concurrencyLimiter';
import type { CurriculumEntityType } from './quizContentAdminService';

function getAuthHeaders(): Record<string, string> {
  return authHeader();
}

// ============================================================
// Common shapes — ADR-B §2
// ============================================================

/**
 * ADR-B §2 "Actors". Every actor field is a pair-plus-name, because
 * `curriculum_change_history` stores only email and role but the queue shows
 * human names. The server resolves `name` from `management` in one
 * `WHERE email IN (…)`.
 *
 * `name` is **null when the account no longer exists**, and ADR-B is explicit
 * that this is a required case rather than an edge case: it happens exactly in
 * the departing-editor scenario the whole feature exists for. Render the email
 * in that case — see `actorDisplayName`.
 */
export interface ApprovalActor {
  email: string;
  role: string;
  name: string | null;
}

/**
 * ADR-B §2 / spec **AT-26c**. Null `name` renders the EMAIL, never "Unknown":
 * null is precisely the departing-editor case, and "Unknown" would erase the one
 * identifier that still resolves. Kept here rather than in a component so the
 * two surfaces (queue and editor) cannot disagree.
 *
 * feat-354: the system actor (role `system`, `system@bespokecast.ai` — the
 * approval backfill and the daily lesson auto-copy) renders as
 * SYSTEM_ACTOR_DISPLAY_NAME, never its email.
 */
export const SYSTEM_ACTOR_DISPLAY_NAME = 'Bespoke (automatic)';

export function actorDisplayName(actor: ApprovalActor | null | undefined): string {
  if (!actor) return '';
  if (actor.role === 'system') return SYSTEM_ACTOR_DISPLAY_NAME;
  const name = actor.name?.trim();
  return name ? name : actor.email;
}

/**
 * The `state` enum on a lesson (ADR-B §3.1). Computed server-side;
 * `design/NOTES.md` §11a: "The client must not derive it from a null timestamp."
 *
 * `never_approved` is distinct from `pending` because the copy and the
 * consequence both differ — nothing at all is on phones — and because
 * NOTES §10.8 forbids field-diffing it at all.
 *
 * Named `…Status` rather than `…State` so it does not collide with ADR-B §2.2's
 * `lesson_state` SHAPE, which is `ApprovalLessonState` below. Two different
 * things called "state" in one contract is worth one rename here.
 */
export type LessonApprovalStatus = 'approved' | 'pending' | 'never_approved';

/**
 * ADR-B §2 `change_kind`. **These are the ADR's wire values, not design's
 * labels.** `design/NOTES.md` §11b writes the triad as `added | changed |
 * removed`; the ADR ships `created | updated | deleted`. The ADR is the
 * contract, so those are the values on the wire and the design words are
 * display strings that map onto them.
 *
 * ⚠ Spec **AT-26b** / NOTES §10.12: a removed rehearsal **criterion** arrives as
 * `"updated"`, not `"deleted"` — it is a value of the rehearsal, not an
 * independently-deletable item, and learners keep being graded on the approved
 * rubric until re-approval. Branch on this field, never on "the criteria list
 * got shorter".
 */
export type ApprovalChangeKind = 'created' | 'updated' | 'deleted';

/** ADR-B §2 `entity_ref`. `drill_id` is null for `learn_lesson` / `learn_drill`. */
export interface ApprovalEntityRef {
  entity_type: CurriculumEntityType;
  entity_id: string;
  lesson_id: string;
  drill_id: string | null;
}

/**
 * ADR-B §3.4 / brief §9.3 — the approve guard token, and it is **OPAQUE**.
 * Round-trip it verbatim: never parse it, never compare two of them, never
 * construct one.
 *
 * It is branded rather than left a bare `string` so that "construct one" is a
 * compile error instead of a code-review question: you cannot pass a string
 * literal where a fingerprint is wanted without writing an explicit cast, and an
 * explicit cast is a deliberate act a reviewer can see. The brand does not stop
 * `a === b`, so the "never compare" half of the rule still rests on this comment
 * and on there being no comparison anywhere in this file.
 *
 * (The r2 token was a bare hex digest. r3 makes it carry per-item digests so the
 * SERVER can answer "what moved" — see `is_new_since_review`. Its content is
 * none of the client's business either way, which is the point of the brand.)
 */
export type PendingFingerprint = string & { readonly __brand: 'pending_fingerprint' };

/**
 * A gated column's approved and draft values.
 *
 * Typed wider than the ADR's string examples because the D4 gated set includes
 * the XP columns (`xp_reward`, `xp_value`, `redo_xp`, `fail_xp`), which are
 * numbers on the wire. Rendering coerces; nothing here compares.
 */
export type ApprovalFieldValue = string | number | boolean | null;

/**
 * ADR-B §3.2, ruling 1: **`fields` carries every gated column, changed or not**,
 * with an explicit `changed` boolean. That is deliberate — design's removed-item
 * panel renders unchanged fields with no change marker, and a changed-only list
 * could not draw it without a second round trip.
 *
 * Ruling 3: on `change_kind: "created"` every `approved_value` is null; on
 * `"deleted"` every `draft_value` is null. That is what lets the panel render
 * "— (not on phones)" and "Removed from phones — already gone" with no
 * per-field special case.
 *
 * Ruling 7 / §2.1: `changed` is server-computed and NORMALIZED while the values
 * are RAW. So `approved_value` and `draft_value` can look identical on screen
 * with `changed: true` (a trailing space), and can look different with
 * `changed: false` — no, they cannot: an item with no `changed: true` field is
 * not in the response at all. Render `changed`; do not check it against the
 * values.
 */
export interface ApprovalField {
  /** The wire column name, e.g. `correct_option`. */
  field: string;
  /**
   * ADR-B §3.2 ruling 5 / NOTES §8: server-computed, and it is the **field's own
   * label** — `correct_option` prints as "Correct answer" because that is what
   * the `<label>` the editor is looking at says.
   */
  label: string;
  changed: boolean;
  approved_value: ApprovalFieldValue;
  draft_value: ApprovalFieldValue;
}

/** ADR-B §3.2. One pending item inside a lesson card. */
export interface ApprovalPendingItem {
  entity_ref: ApprovalEntityRef;
  change_kind: ApprovalChangeKind;
  /**
   * ADR-B §3.2 ruling 5: server-computed from the item's own gated text,
   * truncated to 120 chars, and for a `"deleted"` item it comes from the
   * SNAPSHOT because the live row is gone. Ruling 6: it is **not translated** —
   * the queue is an authoring surface and shows the English source.
   */
  label: string;
  /** `question_type` for questions; null for every other entity type. */
  kind_label: string | null;
  /** Live `sort_order` as a 1-based index within its parent. */
  position: number;
  /** null for `learn_lesson` / `learn_drill`. */
  parent_drill_title: string | null;
  /**
   * Spec AT-26c: this is the **latest** editor only, and a diff can span several
   * edits by several people. Label it "Last edited by", never a bare name.
   */
  changed_by: ApprovalActor | null;
  changed_at: string | null;
  /**
   * Latest `curriculum_change_history` row; null if none.
   *
   * ⚠ This is for **Restore** (below) and for nothing else. It is NOT an input
   * to "is this new since I opened the page" — see `is_new_since_review` and the
   * file header. A null here is ordinary: pendingness is derived from state, so
   * an item can be pending with no history row at all.
   */
  history_id: string | null;
  /**
   * ADR-B §3.2 ruling 4: `_is_revertable` evaluated on that history row, so the
   * frontend never offers a Restore the API would refuse. Restore is **not a new
   * endpoint** — it is the existing `revertCurriculumChange(history_id)`.
   */
  restorable: boolean;
  fields: ApprovalField[];
  /**
   * ADR-B **§3.4.1** — SERVER-SUPPLIED, and required in every item.
   *
   * `true`  = this item appeared, or moved, since the fingerprint the client sent.
   * `false` = present with an identical digest.
   * `null`  = plain read (§3.2 / §3.3) or a 200, where "since review" has no
   *           referent. Not "unknown" — there is no question being asked.
   *
   * Render it. Do not derive it, do not default it, and do not fill a null with a
   * guess: the file header says why the client-side proxy was deleted rather than
   * kept as a fallback. Spec AT-27b names this field.
   */
  is_new_since_review: boolean | null;
}

/**
 * ADR-B **§2.2** — the `lesson_state` shape: the per-lesson header, no `items`.
 * Returned by §3.1's summary, which is the only call the editor page makes.
 */
export interface ApprovalLessonState {
  lesson_id: string;
  title: string;
  emoji: string | null;
  sort_order: number;
  state: LessonApprovalStatus;
  /**
   * NOTES §11a: counted on the **D4 set only**. §10.9 is the matching warning
   * for the client — do NOT reach for `changedSnapshotFields` to recompute this;
   * it diffs `sort_order` and `updated` too, and AT-8 / AT-14 both forbid
   * drag-to-reorder showing as pending. Read this number; never count it.
   */
  pending_item_count: number;
  approved_by: ApprovalActor | null;
  approved_at: string | null;
  last_change_by: ApprovalActor | null;
  last_changed_at: string | null;
  /** ADR-B §3.4. Opaque — see `PendingFingerprint`. */
  pending_fingerprint: PendingFingerprint;
}

/**
 * ADR-B **§2.2** — the `lesson_detail` shape: `lesson_state` **plus** items. A
 * STRICT SUPERSET, deliberately, so a client can widen one to the other and
 * never has to reconcile two overlapping field lists.
 *
 * This is what §3.2 (the queue), §3.3 (one lesson) and §3.4 (approve, on **200
 * and 409 alike**) all return. Spec **AT-27bb**. Before ADR-B r3 the two shapes
 * differed by `sort_order` in one direction and `items` in the other, so neither
 * contained the other; that ambiguity is fixed in the contract rather than
 * worked around here, which is why `items` is REQUIRED below and not `items?`.
 *
 * On a 200 `items` is `[]` and `items_returned` is 0 — honest, because nothing
 * is pending after a successful approve.
 */
export interface ApprovalLessonDetail extends ApprovalLessonState {
  items_returned: number;
  /** true ⇒ fetch §3.3 for the rest ("Show all 8"). */
  items_truncated: boolean;
  items: ApprovalPendingItem[];
}

/** ADR-B §3.1 `last_approval`. Null if the tenant has never approved. */
export interface ApprovalLastApproval {
  lesson_id: string;
  lesson_title: string;
  approved_by: ApprovalActor;
  approved_at: string;
}

/**
 * ADR-B §3.1 response.
 *
 * With `approval_required: false` the ADR says the body is
 * `{"approval_required": false, "can_approve": <bool>, "lessons": []}` and every
 * count is 0 — the backend does no derivation at all, matching D7's *no work*
 * rather than merely *no difference*. The counts are therefore normalized on
 * read (see `normalizeSummary`) so a body that omits them cannot render `NaN`.
 * That is a missing-key default, not a re-derivation: nothing is ever counted
 * from `lessons`.
 */
export interface ApprovalSummary {
  approval_required: boolean;
  /**
   * ADR-B §5: a **rendering hint, not the guard**. The guard is
   * `require_curriculum_approver` server-side and AT-19 tests the API directly.
   * `false` ⇒ render everything except the approve controls (AT-28).
   */
  can_approve: boolean;
  lessons_total: number;
  lessons_pending: number;
  /** Sum of pending items — "4 Lessons waiting · 11 changes". */
  changes_pending: number;
  lessons: ApprovalLessonState[];
  last_approval: ApprovalLastApproval | null;
}

/** ADR-B §3.2 response. */
export interface ApprovalQueue {
  approval_required: boolean;
  can_approve: boolean;
  lessons_pending: number;
  changes_pending: number;
  lessons: ApprovalLessonDetail[];
}

/** ADR-B §3.3 response — `lesson_detail` with `items` unpaged to the window. */
export interface ApprovalLessonPage extends ApprovalLessonDetail {
  items_total: number;
}

/**
 * ADR-B §3.4 success response — `lesson_detail` (§2.2, AT-27bb) plus what the
 * approve cleared.
 */
export interface ApproveLessonResult extends ApprovalLessonDetail {
  cleared_item_count: number;
}

/** ADR-B §3.5 response. */
export interface ApprovalRequiredResult {
  approval_required: boolean;
  backfilled_lessons: number;
  backfilled_items: number;
  approved_by: ApprovalActor;
  approved_at: string;
}

// ============================================================
// Normalization of MISSING keys — not of values
// ============================================================

/**
 * Defaults for counts a flag-off body may legitimately omit (ADR-B §3.1). This
 * fills absent keys; it never recomputes a present one, and it never counts
 * anything from `lessons`. Keeping the two ideas apart matters: deriving
 * `lessons_pending` from `lessons.length` would be a client twin of a server
 * decision, which is the exact shape §2.1 forbids.
 */
function num(value: unknown): number {
  return typeof value === 'number' && isFinite(value) ? value : 0;
}

function normalizeSummary(raw: Partial<ApprovalSummary> | null | undefined): ApprovalSummary {
  return {
    approval_required: raw?.approval_required === true,
    can_approve: raw?.can_approve === true,
    lessons_total: num(raw?.lessons_total),
    lessons_pending: num(raw?.lessons_pending),
    changes_pending: num(raw?.changes_pending),
    lessons: raw?.lessons ?? [],
    last_approval: raw?.last_approval ?? null,
  };
}

// ============================================================
// The endpoints — ADR-B §3
// ============================================================

const basePath = (tenantId: string) =>
  `/api/tenant/${encodeURIComponent(tenantId)}/learn-content/approvals`;

/**
 * ADR-B §3.1 — `GET .../approvals/summary`. Gate: `require_curriculum_editor`.
 *
 * **This is the only call the editor page needs on load** (ADR-B §5), and it
 * drives AT-25 (header badge + per-lesson tree chip), AT-28 (`can_approve`) and
 * AT-29 (`approval_required`). ADR-A evidence T6 puts the whole-tenant
 * derivation at ~1.5 ms, which is what makes it safe to sit on
 * `QuizContentManagement`'s existing load.
 *
 * NOTE the gate: a store manager gets a **200** here, not a 403. Over-gating the
 * reads is the easy mistake and it would make AT-28 untestable (ADR-B §1.2).
 */
export async function getApprovalSummary(tenantId: string): Promise<ApprovalSummary> {
  const raw = await fetchApi<Partial<ApprovalSummary>>(`${basePath(tenantId)}/summary`, {
    headers: getAuthHeaders(),
  });
  return normalizeSummary(raw);
}

/**
 * ADR-B §3.2 — `GET .../approvals/pending`. Gate: `require_curriculum_editor`.
 *
 * The queue, including **every diff**: ADR-B §5 promises no per-item fetch, no
 * `curriculum_change_history` call and no name lookup on top of this one.
 *
 * `itemLimit` is the maximum items returned PER LESSON and defaults server-side
 * to 10 — deliberately, so that the first approval of a fresh 26-lesson tenant
 * (where every item is `"created"`) stays bounded while `pending_item_count`
 * stays exact. `items_truncated` plus `pending_item_count - items_returned` is
 * what renders design's "+ 6 more … Show all 8".
 *
 * Every item's `is_new_since_review` is `null` here: this is a plain read, and
 * "since review" has no referent (§3.4.1).
 */
export async function getApprovalQueue(
  tenantId: string,
  opts?: { itemLimit?: number; lessonsLimit?: number },
): Promise<ApprovalQueue> {
  const params = new URLSearchParams();
  if (opts?.itemLimit !== undefined) params.set('item_limit', String(opts.itemLimit));
  if (opts?.lessonsLimit !== undefined) params.set('lessons_limit', String(opts.lessonsLimit));
  const query = params.toString();
  const raw = await fetchApi<Partial<ApprovalQueue>>(
    `${basePath(tenantId)}/pending${query ? `?${query}` : ''}`,
    { headers: getAuthHeaders() },
  );
  return {
    approval_required: raw?.approval_required === true,
    can_approve: raw?.can_approve === true,
    lessons_pending: num(raw?.lessons_pending),
    changes_pending: num(raw?.changes_pending),
    lessons: raw?.lessons ?? [],
  };
}

/**
 * ADR-B §3.3 — `GET .../approvals/lessons/{lesson_id}`. Gate:
 * `require_curriculum_editor`. What "Show all 8" and design state E's 47-item
 * card fetch. 404 if the lesson is not in `tenantId` (tenancy from the row,
 * AT-20).
 *
 * ⚠ ASSUMPTION, recorded rather than guessed silently (`QUESTIONS.md` Q8, and it
 * survives r3 unaddressed). ADR-B §3.3 says "Response: the same `lesson` object
 * as §3.2 … plus `items_total`", which reads as the BARE lesson object, whereas
 * §3.2 wraps its lessons in `{lessons: […]}`. The plain reading is bare, so that
 * is what this takes; the one-line unwrap below also accepts a `{lesson: …}`
 * envelope so a wrapped body degrades into a correct render instead of a crash.
 * Delete the unwrap once the ADR says which. This is an ENVELOPE tolerance, not
 * a value default — it invents nothing.
 */
export async function getApprovalLessonDetail(
  tenantId: string,
  lessonId: string,
  opts?: { limit?: number; offset?: number },
): Promise<ApprovalLessonPage> {
  const params = new URLSearchParams();
  if (opts?.limit !== undefined) params.set('limit', String(opts.limit));
  if (opts?.offset !== undefined) params.set('offset', String(opts.offset));
  const query = params.toString();
  const raw = await fetchApi<ApprovalLessonPage | { lesson: ApprovalLessonPage }>(
    `${basePath(tenantId)}/lessons/${encodeURIComponent(lessonId)}${query ? `?${query}` : ''}`,
    { headers: getAuthHeaders() },
  );
  return unwrapLesson(raw);
}

function unwrapLesson<T>(raw: T | { lesson: T }): T {
  if (raw && typeof raw === 'object' && 'lesson' in (raw as Record<string, unknown>)) {
    return (raw as { lesson: T }).lesson;
  }
  return raw as T;
}

// ============================================================
// Approve — ADR-B §3.4
// ============================================================

/** ADR-B §4: the machine-readable reason that distinguishes this 409 from any other. */
export const CHANGED_SINCE_REVIEW = 'changed_since_review';

/**
 * ADR-B §3.4 / §4 — `409 changed_since_review`.
 *
 * **This is not an error and must not be dressed as one** (NOTES §6a, spec
 * AT-27b). The guard WORKED: someone edited the lesson between the queue load
 * and the click, and nothing was approved. The queue redraws the refreshed
 * lesson and marks what moved; the copy leads with what did *not* happen.
 *
 * Thrown rather than returned so the ordinary single-lesson path reads normally;
 * `approveLessons` catches it and turns it into one arm of a partial-success
 * result (AT-27c).
 *
 * `Object.setPrototypeOf` is required, not decoration — this project targets ES5
 * (`tsconfig.json`), where subclassing leaves the instance's prototype as
 * `Error.prototype` and `instanceof` silently returns false. Same reason
 * `ApiError` does it.
 */
export class ChangedSinceReviewError extends Error {
  readonly lessonId: string;
  /**
   * The refreshed lesson, as `lesson_detail` — ADR-B §2.2 and §4, spec AT-27bb.
   * REQUIRED, and `items` on it is required too.
   *
   * **Redraw straight from this. Issue no follow-up §3.3 fetch.** Each item
   * carries the server's `is_new_since_review`, which is the whole reason the
   * 409 is worth showing: without it the approver re-reads four diffs looking
   * for the one that changed, fails to find it, and clicks through — and a 409
   * retried blind has exactly the same outcome as no 409 at all.
   *
   * A 409 whose body carries no `lesson` is a contract violation, not a state to
   * render: `approveLesson` leaves that on the ordinary error path rather than
   * fabricating an empty card. (r2 typed this nullable with optional `items`,
   * against an ADR that had not yet said which shape it was. r3 says.)
   */
  readonly lesson: ApprovalLessonDetail;

  constructor(lessonId: string, lesson: ApprovalLessonDetail) {
    super(CHANGED_SINCE_REVIEW);
    this.name = 'ChangedSinceReviewError';
    this.lessonId = lessonId;
    this.lesson = lesson;
    Object.setPrototypeOf(this, ChangedSinceReviewError.prototype);
  }
}

type ConflictBody = {
  detail?: unknown;
  lesson?: ApprovalLessonDetail | { lesson: ApprovalLessonDetail };
};

/**
 * ADR-B §4 says the 409 `detail` is the literal string `changed_since_review`.
 * FastAPI can also nest it, so both are accepted — the point of the reason code
 * is that the UI can pick the right copy, and a shape difference must not send a
 * successful guard down the generic-error path (NOTES §6a).
 */
function isChangedSinceReview(err: ApiError): boolean {
  if (err.status !== 409) return false;
  const body = err.body as ConflictBody | null;
  if (typeof body?.detail === 'string') return body.detail === CHANGED_SINCE_REVIEW;
  return err.message === CHANGED_SINCE_REVIEW;
}

/**
 * ADR-B §3.4 — `POST .../approvals/lessons/{lesson_id}/approve`. Gate:
 * **`require_curriculum_approver`**. AT-18, AT-19, AT-20, AT-21, AT-22.
 *
 * ONE LESSON PER CALL. Not a bulk endpoint — ADR-B §3.4 rules it and AT-27
 * requires it: "Bulk-selecting lessons and approving issues one approve call per
 * lesson". `approveLessons` below is the fan-out.
 *
 * `pendingFingerprint` is REQUIRED whenever the lesson has anything pending, and
 * omitting it then is a **400**, deliberately: "an optional guard on a
 * security-shaped action is a guard nobody sends". Omitted with nothing pending
 * is the AT-21 idempotent no-op. Callers pass the fingerprint they RENDERED —
 * never one re-fetched immediately before the click, which would defeat the
 * guard by re-reading the very edit the approver did not see.
 *
 * @throws {ChangedSinceReviewError} on 409 — the guard firing, not a failure.
 */
export async function approveLesson(
  tenantId: string,
  lessonId: string,
  pendingFingerprint?: PendingFingerprint,
): Promise<ApproveLessonResult> {
  const body: Record<string, unknown> = {};
  if (pendingFingerprint !== undefined) {
    body.pending_fingerprint = pendingFingerprint;
  }
  try {
    return await fetchApi<ApproveLessonResult>(
      `${basePath(tenantId)}/lessons/${encodeURIComponent(lessonId)}/approve`,
      { method: 'POST', headers: getAuthHeaders(), body },
    );
  } catch (err) {
    if (err instanceof ApiError && isChangedSinceReview(err)) {
      const conflict = err.body as ConflictBody | null;
      const lesson = conflict?.lesson ? unwrapLesson(conflict.lesson) : null;
      // A 409 with no lesson cannot be redrawn and must not be faked into an
      // empty card — that would read as "nothing is pending" on the one screen
      // whose job is to say what moved. Fall through to the generic error path.
      if (lesson) {
        throw new ChangedSinceReviewError(lessonId, lesson);
      }
    }
    throw err;
  }
}

/**
 * ADR-B §3.5 — `PUT .../approvals/required`. Gate: **`require_admin`**, not
 * `require_curriculum_approver`: a corporate manager who could turn the gate off
 * could clear their own pending changes by disabling the feature, which defeats
 * D6 entirely.
 *
 * `false → true` performs the flip AND the grandfather backfill in one
 * transaction (AT-23). No surface in this slice calls it — it is typed here
 * because it is one of ADR-B's five endpoints and because the next person to
 * build the ops screen should not re-derive the shape.
 */
export async function setApprovalRequired(
  tenantId: string,
  required: boolean,
): Promise<ApprovalRequiredResult> {
  return fetchApi<ApprovalRequiredResult>(`${basePath(tenantId)}/required`, {
    method: 'PUT',
    headers: getAuthHeaders(),
    body: { required },
  });
}

// ============================================================
// Bulk approve — the fan-out. AT-27, AT-27c
// ============================================================

/** One lesson's outcome in a fan-out. Discriminated so no arm can be dropped. */
export type ApproveOutcome =
  | { status: 'approved'; lessonId: string; title: string; result: ApproveLessonResult }
  | { status: 'conflict'; lessonId: string; title: string; conflict: ChangedSinceReviewError }
  | { status: 'failed'; lessonId: string; title: string; error: unknown };

export interface ApproveSelectionResult {
  approved: Array<Extract<ApproveOutcome, { status: 'approved' }>>;
  conflicts: Array<Extract<ApproveOutcome, { status: 'conflict' }>>;
  failed: Array<Extract<ApproveOutcome, { status: 'failed' }>>;
  outcomes: ApproveOutcome[];
}

/**
 * AT-27 / AT-27c — bulk selection fans out to **one approve call per lesson**
 * and every arm is rendered.
 *
 * "A selection of five where one is refused shows four approved and one needing
 * re-review — not a single failure banner, and not a silent success." So this
 * never rejects: it resolves with all three buckets, and the caller renders each
 * naming its lessons. NOTES §6a: "A single 'Something went wrong' after
 * approving 2 of 3 leaves the reader unable to tell which two went live — and
 * they went live."
 *
 * Concurrency is capped at 4 for the same reason `createLimiter` exists — a
 * 26-lesson select-all would otherwise open 26 sockets at once against a write
 * path that takes a transaction per call. Order of `outcomes` follows the input
 * selection, not completion order, so the strip lists lessons the way the reader
 * selected them.
 *
 * After this resolves the caller re-fetches §3.1 once (ADR-B §5) — the badge
 * must update **without a page reload** (AT-27). The conflicted cards redraw
 * from their own 409 bodies, NOT from that refetch: the refetch is a fresh read,
 * where `is_new_since_review` is null and "what moved" is unanswerable.
 */
export async function approveLessons(
  tenantId: string,
  lessons: Array<{ lesson_id: string; title: string; pending_fingerprint: PendingFingerprint }>,
  opts?: { maxConcurrent?: number },
): Promise<ApproveSelectionResult> {
  const limit = createLimiter(opts?.maxConcurrent ?? 4);

  const outcomes = await Promise.all(
    lessons.map((lesson) =>
      limit<ApproveOutcome>(async () => {
        try {
          const result = await approveLesson(tenantId, lesson.lesson_id, lesson.pending_fingerprint);
          return { status: 'approved', lessonId: lesson.lesson_id, title: lesson.title, result };
        } catch (err) {
          if (err instanceof ChangedSinceReviewError) {
            return {
              status: 'conflict',
              lessonId: lesson.lesson_id,
              title: lesson.title,
              conflict: err,
            };
          }
          return { status: 'failed', lessonId: lesson.lesson_id, title: lesson.title, error: err };
        }
      }),
    ),
  );

  return {
    approved: outcomes.filter(
      (o): o is Extract<ApproveOutcome, { status: 'approved' }> => o.status === 'approved',
    ),
    conflicts: outcomes.filter(
      (o): o is Extract<ApproveOutcome, { status: 'conflict' }> => o.status === 'conflict',
    ),
    failed: outcomes.filter(
      (o): o is Extract<ApproveOutcome, { status: 'failed' }> => o.status === 'failed',
    ),
    outcomes,
  };
}
